import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceApi, type Invoice, type InvoiceStatus } from '../api/workspace';
import { Loading, PageError } from '../components/State';
import { StatusPill } from './InvoicesPage';
import { isApiError } from '../lib/apiClient';
import { fmtDate, money } from '../lib/format';
import './InvoicesPage.css';
import './ShiftDetailsPage.css';

type NavState = { invoice?: Invoice; youthName?: string; connectionId?: string } | null;

const NEXT_ACTIONS: Record<InvoiceStatus, { to: InvoiceStatus; label: string; primary?: boolean }[]> = {
  draft: [
    { to: 'sent', label: 'Send to caregiver', primary: true },
    { to: 'cancelled', label: 'Cancel invoice' },
  ],
  sent: [
    { to: 'paid', label: 'Mark as paid', primary: true },
    { to: 'cancelled', label: 'Cancel invoice' },
  ],
  paid: [],
  cancelled: [],
};

export function InvoiceDetailsPage() {
  const { invoiceId = '' } = useParams();
  const nav = useLocation().state as NavState;
  const qc = useQueryClient();

  const needFallback = !nav?.invoice || nav.invoice.id !== invoiceId;
  const fallback = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const clients = await workspaceApi.clients();
      const per = await Promise.all(
        clients.map(async (c) => {
          const list = await workspaceApi.invoices(c.connectionId).catch(() => [] as Invoice[]);
          return list.map((i) => ({ ...i, youthName: c.youthSnapshot?.name ?? 'Youth' }));
        }),
      );
      return per.flat();
    },
    enabled: needFallback,
  });

  const found = needFallback ? fallback.data?.find((i) => i.id === invoiceId) : nav?.invoice;
  const invoice = found;
  const connectionId = needFallback
    ? (found as { connectionId?: string } | undefined)?.connectionId
    : nav?.connectionId;
  const youthName =
    (needFallback ? (found as { youthName?: string } | undefined)?.youthName : nav?.youthName) ??
    'Youth';

  const mutate = useMutation({
    mutationFn: (to: InvoiceStatus) =>
      workspaceApi.setInvoiceStatus(connectionId!, invoiceId, to),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      fallback.refetch();
    },
  });

  if (needFallback && fallback.isLoading) return <Loading />;

  if (!invoice) {
    return (
      <div>
        <Link to="/invoices" className="yp__back">
          ‹ Invoices
        </Link>
        <PageError error={{ status: 404, message: 'Invoice not found.' }} />
      </div>
    );
  }

  const current = mutate.data ?? invoice;
  const actions = NEXT_ACTIONS[current.status];

  return (
    <div className="sd">
      <Link to="/invoices" className="yp__back">
        ‹ Invoices
      </Link>
      <div className="page-head">
        <h1>{current.invoiceNumber}</h1>
        <StatusPill status={current.status} />
      </div>

      <dl className="sd__grid">
        <Field label="Family" value={youthName} />
        <Field label="Total" value={money(current.totalAmount, current.currency)} />
        <Field label="Due" value={current.dueDate ? fmtDate(current.dueDate) : '—'} />
        <Field label="Created" value={fmtDate(current.createdAt.split('T')[0])} />
        {current.paidAt && (
          <Field label="Paid" value={fmtDate(current.paidAt.split('T')[0])} />
        )}
      </dl>

      <section className="sd__section">
        <h2>Line items</h2>
        <div className="card inv__list">
          {invoice.lineItems.map((li, i) => (
            <div key={i} className="inv__row">
              <div className="inv__row-main">
                <div className="inv__row-num">{li.description}</div>
                <div className="muted inv__row-sub">
                  {li.quantity} × {money(li.unitPrice, invoice.currency)}
                </div>
              </div>
              <div className="inv__row-amt">{money(li.quantity * li.unitPrice, invoice.currency)}</div>
            </div>
          ))}
          <div className="inv__row">
            <div className="inv__row-main inv__row-num">Total</div>
            <div className="inv__row-amt">{money(invoice.totalAmount, invoice.currency)}</div>
          </div>
        </div>
      </section>

      {invoice.notes && (
        <section className="sd__section">
          <h2>Notes</h2>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{invoice.notes}</p>
        </section>
      )}

      {actions.length > 0 && connectionId && (
        <section className="sd__section">
          <h2>Actions</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {actions.map((a) => (
              <button
                key={a.to}
                className={a.primary ? 'btn' : 'btn btn--ghost'}
                disabled={mutate.isPending}
                onClick={() => {
                  if (a.to === 'cancelled' && !confirm('Cancel this invoice?')) return;
                  mutate.mutate(a.to);
                }}
              >
                {mutate.isPending ? 'Updating…' : a.label}
              </button>
            ))}
          </div>
          {mutate.isError && (
            <p className="error-text">
              {isApiError(mutate.error) ? mutate.error.message : 'Could not update the invoice.'}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="sd__field">
      <dt className="muted">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
